import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  requirementType: string;
  requirementValue: number;
  rewardPoints: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  category: string;
  enabled: boolean;
  createdAt: Date;
}

const Achievements: React.FC = () => {
  const { t } = useTranslation();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Achievement | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    icon: '🏆',
    requirementType: 'wins',
    requirementValue: 10,
    rewardPoints: 100,
    rarity: 'rare' as Achievement['rarity'],
    category: 'general',
    enabled: true
  });

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'achievements'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      })) as Achievement[];
      setAchievements(data);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...form,
        updatedAt: Timestamp.now()
      };

      if (editing) {
        await updateDoc(doc(db, 'achievements', editing.id), data);
      } else {
        await addDoc(collection(db, 'achievements'), {
          ...data,
          createdAt: Timestamp.now()
        });
      }

      setShowModal(false);
      setEditing(null);
      resetForm();
      loadAchievements();
    } catch (error) {
      console.error('Error saving achievement:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('leaderboard.confirmDeleteAchievement'))) {
      try {
        await deleteDoc(doc(db, 'achievements', id));
        loadAchievements();
      } catch (error) {
        console.error('Error deleting achievement:', error);
      }
    }
  };

  const handleEdit = (achievement: Achievement) => {
    setEditing(achievement);
    setForm({
      name: achievement.name,
      description: achievement.description,
      icon: achievement.icon,
      requirementType: achievement.requirementType,
      requirementValue: achievement.requirementValue,
      rewardPoints: achievement.rewardPoints,
      rarity: achievement.rarity,
      category: achievement.category,
      enabled: achievement.enabled
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      icon: '🏆',
      requirementType: 'wins',
      requirementValue: 10,
      rewardPoints: 100,
      rarity: 'rare',
      category: 'general',
      enabled: true
    });
  };

  const getRarityColor = (rarity: Achievement['rarity']) => {
    switch (rarity) {
      case 'common': return 'bg-gray-500/20 text-gray-400';
      case 'rare': return 'bg-blue-500/20 text-blue-400';
      case 'epic': return 'bg-purple-500/20 text-purple-400';
      case 'legendary': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('leaderboard.achievementManagement')}</h3>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('leaderboard.newAchievement')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {achievements.map(achievement => (
          <div key={achievement.id} className="card-dark p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{achievement.icon}</span>
                <div>
                  <h4 className="text-lg font-bold text-white">{achievement.name}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getRarityColor(achievement.rarity)}`}>
                    {t(`leaderboard.rarity.${achievement.rarity}`)}
                  </span>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                achievement.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
              }`}>
                {achievement.enabled ? t('leaderboard.enabled') : t('leaderboard.disabled')}
              </span>
            </div>

            <p className="text-sm text-[#B8B8C3] mb-4">{achievement.description}</p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#B8B8C3]">{t('leaderboard.requirement')}:</span>
                <span className="text-white font-semibold">
                  {achievement.requirementValue} {achievement.requirementType}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#B8B8C3]">{t('leaderboard.reward')}:</span>
                <span className="text-cyan-400 font-bold">
                  {achievement.rewardPoints} {t('leaderboard.points')}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(achievement)}
                className="flex-1 btn-secondary text-sm"
              >
                {t('common.edit')}
              </button>
              <button
                onClick={() => handleDelete(achievement.id)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {achievements.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🏅</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('leaderboard.noAchievements')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('leaderboard.noAchievementsDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('leaderboard.createFirstAchievement')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('leaderboard.editAchievement') : t('leaderboard.newAchievement')}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.icon')}
                  </label>
                  <input
                    type="text"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className="input-dark w-full text-center text-2xl"
                    maxLength={2}
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.achievementName')}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-dark w-full"
                    placeholder={t('leaderboard.achievementNamePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('leaderboard.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder={t('leaderboard.achievementDescPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.requirementType')}
                  </label>
                  <input
                    type="text"
                    value={form.requirementType}
                    onChange={(e) => setForm({ ...form, requirementType: e.target.value })}
                    className="input-dark w-full"
                    placeholder="e.g., wins, matches, points"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.requirementValue')}
                  </label>
                  <input
                    type="number"
                    value={form.requirementValue}
                    onChange={(e) => setForm({ ...form, requirementValue: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.rewardPoints')}
                  </label>
                  <input
                    type="number"
                    value={form.rewardPoints}
                    onChange={(e) => setForm({ ...form, rewardPoints: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('common.rarity')}
                  </label>
                  <select
                    value={form.rarity}
                    onChange={(e) => setForm({ ...form, rarity: e.target.value as Achievement['rarity'] })}
                    className="input-dark w-full"
                  >
                    <option value="rare">{t('leaderboard.rarity.rare')}</option>
                    <option value="common">{t('leaderboard.rarity.common')}</option>
                    <option value="epic">{t('leaderboard.rarity.epic')}</option>
                    <option value="legendary">{t('leaderboard.rarity.legendary')}</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="achievementEnabled"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="achievementEnabled" className="text-sm text-[#B8B8C3]">
                  {t('leaderboard.enableAchievement')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Achievements;
